import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import styles from './FloatingWindows.module.css';
import { X, Minus, Move, Filter, Search } from 'lucide-react';

const STORAGE_KEY = 'gantt_floating_windows_state';

// Podkomponent zawartości okna z filtrowaniem po statusie oraz wyszukiwaniem nr/rok
const WindowTaskContent = React.memo(({ rawStages, statusFilter, searchQuery, onSelectStage }) => {
  const sortedAndFilteredStages = useMemo(() => {
    if (!rawStages) return [];
    
    // 1. Sortowanie chronologiczne wg daty rozpoczęcia
    let result = [...rawStages].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    // 2. Filtrowanie po statusie
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter((stage) => String(stage.status) === String(statusFilter));
    }

    // 3. Wyszukiwanie po numerze (nr) i roku (rok)
    if (searchQuery && searchQuery.trim() !== '') {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((stage) => {
        const nrStr = String(stage.nr || '').toLowerCase();
        const rokStr = String(stage.rok || '').toLowerCase();
        const fullFormat = `${nrStr}/${rokStr}`;

        return nrStr.includes(q) || rokStr.includes(q) || fullFormat.includes(q);
      });
    }

    return result;
  }, [rawStages, statusFilter, searchQuery]);

  const getStatusBadge = (status) => {
    switch (Number(status)) {
      case 1:
        return <span className={`${styles.badge} ${styles.badgePlan}`}>Plan</span>;
      case 2:
        return <span className={`${styles.badge} ${styles.badgeProgress}`}>W trakcie</span>;
      case 3:
        return <span className={`${styles.badge} ${styles.badgeHold}`}>Wstrzymane</span>;
      case 4:
        return <span className={`${styles.badge} ${styles.badgeDone}`}>Zakończone</span>;
      default:
        return null;
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '--:--';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  };

  return (
    <div className={styles.taskList}>
      {sortedAndFilteredStages.length === 0 ? (
        <div className={styles.emptyState}>Brak zadań spełniających kryteria</div>
      ) : (
        sortedAndFilteredStages.map((s, idx) => (
          <div
            key={s.global_id || idx}
            className={styles.taskItem}
            onClick={() => onSelectStage && onSelectStage(s)}
            title="Kliknij, aby zaznaczyć na wykresie Gantta"
          >
            <div className={styles.taskHeaderRow}>
              <span className={styles.taskTime}>{formatDateShort(s.start)}</span>
              {getStatusBadge(s.status)}
            </div>
            <div className={styles.taskBodyRow}>
              <span className={styles.taskClient} title={s.firma_nazwa}>
                {s.firma_nazwa || 'Przerwa'}
              </span>
              <span className={styles.taskOrder}>
                {s.nr ? `${s.nr}/${s.rok}` : ''}
              </span>
            </div>
            {s.tytul && (
              <div className={styles.taskTitle} title={s.tytul}>
                {s.tytul}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
});

export const FloatingWindows = forwardRef(({ procesory, groupedStages, onSelectStage, onWindowsChange }, ref) => {
  const [windows, setWindows] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Błąd odczytu localStorage:', e);
      return [];
    }
  });

  const dragRef = useRef(null);
  const animFrameRef = useRef(null);

  const openWindowsSignature = useMemo(() => {
    return windows
      .filter((w) => w.isOpen)
      .map((w) => `${w.id}-${w.isMinimized}`)
      .join('|');
  }, [windows]);

  useEffect(() => {
    if (onWindowsChange) {
      onWindowsChange(windows.filter((w) => w.isOpen));
    }
  }, [openWindowsSignature, onWindowsChange]);

  const saveToLocalStorage = (newWindows) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newWindows));
    } catch (e) {
      console.error('Błąd zapisu do localStorage:', e);
    }
  };

  const bringToFront = (id) => {
    setWindows((prev) => {
      const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
      const updated = prev.map((w) =>
        w.id === id ? { ...w, isOpen: true, isMinimized: false, zIndex: maxZ + 1 } : w
      );
      saveToLocalStorage(updated);
      return updated;
    });
  };

  const closeWindow = (id) => {
    setWindows((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, isOpen: false } : w));
      saveToLocalStorage(updated);
      return updated;
    });
  };

  const closeAll = () => {
    setWindows((prev) => {
      const updated = prev.map((w) => ({ ...w, isOpen: false }));
      saveToLocalStorage(updated);
      return updated;
    });
  };

  const addWindow = (procesorId) => {
    const procIdNum = Number(procesorId);
    setWindows((prev) => {
      const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
      const existing = prev.find((w) => Number(w.procesor_id) === procIdNum);

      let updated;
      if (existing) {
        updated = prev.map((w) =>
          w.id === existing.id
            ? { ...w, isOpen: true, isMinimized: false, zIndex: maxZ + 1 }
            : w
        );
      } else {
        const proc = procesory?.find((p) => Number(p.id) === procIdNum);
        const newWin = {
          id: `win-proc-${procIdNum}-${Date.now()}`,
          title: proc?.nazwa || `Procesor ${procIdNum}`,
          procesor_id: procIdNum,
          x: 80 + (prev.length * 25) % 200,
          y: 80 + (prev.length * 25) % 150,
          width: 360,
          height: 280,
          isOpen: true,
          isMinimized: false,
          zIndex: maxZ + 1,
          statusFilter: 'all',
          searchQuery: '',
        };
        updated = [...prev, newWin];
      }
      saveToLocalStorage(updated);
      return updated;
    });
  };

  useImperativeHandle(ref, () => ({
    addWindow,
    closeWindow,
    bringToFront,
    closeAll,
    centerWindow,
  }));

  const handleMouseMove = (e) => {
    if (!dragRef.current) return;

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }

    const { type, id, startX, startY, initialX, initialY, initialWidth, initialHeight } = dragRef.current;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    animFrameRef.current = requestAnimationFrame(() => {
      setWindows((prev) =>
        prev.map((w) => {
          if (w.id !== id) return w;

          if (type === 'move') {
            return {
              ...w,
              x: Math.max(0, initialX + deltaX),
              y: Math.max(0, initialY + deltaY),
            };
          }

          if (type === 'resize') {
            return {
              ...w,
              width: Math.max(280, initialWidth + deltaX),
              height: Math.max(140, initialHeight + deltaY),
            };
          }

          return w;
        })
      );
    });
  };

  const handleMouseUp = () => {
    if (dragRef.current) {
      dragRef.current = null;
      setWindows((latest) => {
        saveToLocalStorage(latest);
        return latest;
      });
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDownHeader = (e, id) => {
    if (e.target.closest('button') || e.target.closest('select') || e.target.closest('input')) return;
    bringToFront(id);

    const win = windows.find((w) => w.id === id);
    if (!win) return;

    dragRef.current = {
      type: 'move',
      id,
      startX: e.clientX,
      startY: e.clientY,
      initialX: win.x,
      initialY: win.y,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDownResize = (e, id) => {
    e.stopPropagation();
    bringToFront(id);

    const win = windows.find((w) => w.id === id);
    if (!win) return;

    dragRef.current = {
      type: 'resize',
      id,
      startX: e.clientX,
      startY: e.clientY,
      initialWidth: win.width,
      initialHeight: win.height,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const toggleMinimize = (id) => {
    setWindows((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, isMinimized: !w.isMinimized } : w));
      saveToLocalStorage(updated);
      return updated;
    });
  };

  const handleFilterChange = (id, filterValue) => {
    setWindows((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, statusFilter: filterValue } : w));
      saveToLocalStorage(updated);
      return updated;
    });
  };

  const handleSearchChange = (id, searchValue) => {
    setWindows((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, searchQuery: searchValue } : w));
      saveToLocalStorage(updated);
      return updated;
    });
  };

const centerWindow = (id) => {
  setWindows((prev) => {
    const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Sztywne ustawienie domyślnego rozmiaru
    const winWidth = 600;
    const winHeight = 400;

    const updated = prev.map((w) => {
      if (w.id === id) {
        const newX = Math.max(20, Math.round((viewportWidth - winWidth) / 2));
        const newY = Math.max(20, Math.round((viewportHeight - winHeight) / 2));

        return {
          ...w,
          width: winWidth,   // Ustawia szerokość na 340px
          height: winHeight, // Ustawia wysokość na 280px
          x: newX,
          y: newY,
          isOpen: true,
          isMinimized: false,
          zIndex: maxZ + 1,
        };
      }
      return w;
    });

    saveToLocalStorage(updated);
    return updated;
  });
};

  return (
    <div className={styles.windowsOverlay}>
      {windows
        .filter((w) => w.isOpen)
        .map((win) => {
          const procName =
            procesory?.find((p) => Number(p.id) === Number(win.procesor_id))?.nazwa || win.title;
          const rawStages = groupedStages?.[win.procesor_id] || [];

          return (
            <div
              key={win.id}
              className={styles.window}
              style={{
                left: `${win.x}px`,
                top: `${win.y}px`,
                width: `${win.width}px`,
                height: win.isMinimized ? 'auto' : `${win.height}px`,
                zIndex: win.zIndex || 10,
              }}
              onMouseDown={() => bringToFront(win.id)}
            >
              {/* Pasek nagłówkowy z wbudowaną wyszukiwarką */}
              <div
                className={styles.windowHeader}
                onMouseDown={(e) => handleMouseDownHeader(e, win.id)}
              >
                <div className={styles.windowTitle}>
                  <Move size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                  <span className={styles.titleText}>{procName}</span>
                </div>

                {/* Wyszukiwarka przypięta do nagłówka */}
                <div className={styles.headerSearch}>
                  <Search size={12} style={{ marginRight: 4, color: '#aaa', flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Szukaj nr/rok..."
                    value={win.searchQuery || ''}
                    onChange={(e) => handleSearchChange(win.id, e.target.value)}
                    className={styles.headerSearchInput}
                  />
                  {win.searchQuery && (
                    <button
                      onClick={() => handleSearchChange(win.id, '')}
                      className={styles.clearSearchBtn}
                      title="Wyczyszczenie wyszukiwania"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                <div className={styles.windowControls}>
                  <button onClick={() => toggleMinimize(win.id)} title="Minimalizuj">
                    <Minus size={14} />
                  </button>
                  <button onClick={() => closeWindow(win.id)} title="Zamknij">
                    <X size={14} />
                  </button>
                </div>
              </div>

              {!win.isMinimized && (
                <div className={styles.windowContent}>
                  {/* Pasek filtra statusu */}
                  <div className={styles.filterToolbar}>
                    {/* <div className={styles.filterGroup}>
                      <Filter size={13} style={{ marginRight: 4, color: '#666' }} />
                      <select
                        value={win.statusFilter || 'all'}
                        onChange={(e) => handleFilterChange(win.id, e.target.value)}
                        className={styles.filterSelect}
                      >
                        <option value="all">Wszystkie ({rawStages.length})</option>
                        <option value="2">W trakcie</option>
                        <option value="1">Zaplanowane</option>
                        <option value="3">Wstrzymane</option>
                        <option value="4">Zakończone</option>
                      </select>
                    </div> */}
                  </div>

                  <WindowTaskContent
                    rawStages={rawStages}
                    statusFilter={win.statusFilter}
                    searchQuery={win.searchQuery}
                    onSelectStage={onSelectStage}
                  />
                </div>
              )}

              {!win.isMinimized && (
                <div
                  className={styles.resizeHandle}
                  onMouseDown={(e) => handleMouseDownResize(e, win.id)}
                />
              )}
            </div>
          );
        })}
    </div>
  );
});

FloatingWindows.displayName = 'FloatingWindows';